export const DEFAULT_INPUT_EXCLUDE_SYMBOLS =
  '[;:<>"\'+!{}\\[\\]\\^]|(\u00A9|\u00AE|[\u2000-\u3300]|\\p{Extended_Pictographic}|\\p{Regional_Indicator})'

export const Locales = {
  nl: 'nl',
  en: 'en',
  de: 'de',
  fr: 'fr',
  es: 'es',
  sv: 'sv',
  da: 'da',
  it: 'it',
  pl: 'pl',
  pt: 'pt',
  fi: 'fi',
  no: 'no',
  cs: 'cs'
} as const

export type LocaleKey = keyof typeof Locales
export type LocaleRecord<T> = Record<LocaleKey, T>


export const LocaleNames: LocaleRecord<string> = {
  nl: 'Nederlands',
  en: 'English',
  de: 'Deutsch',
  da: 'Dansk',
  fr: 'Français',
  es: 'Español',
  sv: 'Svenska',
  it: 'Italiano',
  pl: 'Polskie',
  pt: 'Português',
  fi: 'Suomi',
  no: 'Norsk',
  cs: 'České'
} as const

export const LocaleIcons: LocaleRecord<string> = {
  nl: 'nl',
  en: 'en',
  de: 'de',
  da: 'da',
  fr: 'fr',
  es: 'es',
  sv: 'se',
  it: 'it',
  pl: 'pl',
  pt: 'pt',
  fi: 'fi',
  no: 'no',
  cs: 'cs'
} as const

export const CountryNames: CountryRecord<string> = {
  at: 'Countries.at',
  be: 'Countries.be',
  de: 'Countries.de',
  dk: 'Countries.dk',
  es: 'Countries.es',
  fr: 'Countries.fr',
  nl: 'Countries.nl',
  gb: 'Countries.gb',
  se: 'Countries.se',
  it: 'Countries.it',
  pl: 'Countries.pl',
  ch: 'Countries.ch',
  ie: 'Countries.ie',
  lu: 'Countries.lu',
  pt: 'Countries.pt',
  fi: 'Countries.fi',
  no: 'Countries.no',
  cz: 'Countries.cz'
} as const

export type CountryCodeKey = keyof typeof CountryNames

export const CountrySelectOptions = Object.entries(CountryNames).map(([key, value]) => ({
  svg: key,
  label: value,
  value: key
}))

export const CountryCodes = {
  austria: 'at',
  belgium: 'be',
  denmark: 'dk',
  germany: 'de',
  spain: 'es',
  france: 'fr',
  netherlands: 'nl',
  unitedKingdom: 'gb',
  sweden: 'se',
  italy: 'it',
  poland: 'pl',
  switzerland: 'ch',
  ireland: 'ie',
  luxembourg: 'lu',
  portugal: 'pt',
  finland: 'fi',
  norway: 'no',
  czechRepublic: 'cz'
} as const

export type CountryCodeName = keyof typeof CountryCodes
export type CountryCodeValue = (typeof CountryCodes)[CountryCodeName]
export type CurrencyCodeValue = (typeof Currencies)[keyof typeof Currencies]

export type CountryRecord<T> = Record<CountryCodeValue, T>

export const NewCountries: CountryCodeValue[] = []

export const WomenCountries: CountryCodeValue[] = [
  CountryCodes.netherlands,
  CountryCodes.belgium,
  CountryCodes.france,
  CountryCodes.germany,
  CountryCodes.austria,
  CountryCodes.unitedKingdom,
  CountryCodes.denmark,
  CountryCodes.finland,
  CountryCodes.sweden,
  CountryCodes.norway,
  CountryCodes.czechRepublic,
  CountryCodes.switzerland
]

export const MailboxDeliveryCountries: CountryCodeValue[] = [CountryCodes.germany, CountryCodes.switzerland]

export const CountrySelectOptionsLimited = Object.entries(CountryNames)
  .filter(([key]) => WomenCountries.includes(key as CountryCodeValue))
  .map(([key, value]) => ({
    svg: key,
    label: value,
    value: key
  }))

export const Currencies = {
  [CountryCodes.austria]: 'EUR',
  [CountryCodes.belgium]: 'EUR',
  [CountryCodes.denmark]: 'DKK',
  [CountryCodes.germany]: 'EUR',
  [CountryCodes.spain]: 'EUR',
  [CountryCodes.france]: 'EUR',
  [CountryCodes.netherlands]: 'EUR',
  [CountryCodes.unitedKingdom]: 'GBP',
  [CountryCodes.sweden]: 'SEK',
  [CountryCodes.italy]: 'EUR',
  [CountryCodes.poland]: 'PLN',
  [CountryCodes.switzerland]: 'CHF',
  [CountryCodes.ireland]: 'EUR',
  [CountryCodes.luxembourg]: 'EUR',
  [CountryCodes.portugal]: 'EUR',
  [CountryCodes.finland]: 'EUR',
  [CountryCodes.norway]: 'NOK',
  [CountryCodes.czechRepublic]: 'CZK'
} as const

export const CountryIds: CountryRecord<number> = {
  nl: 1,
  be: 2,
  de: 3,
  at: 4,
  gb: 5,
  es: 6,
  fr: 7,
  se: 8,
  dk: 9,
  it: 10,
  pl: 11,
  ch: 12,
  lu: 13,
  ie: 14,
  pt: 15,
  fi: 16,
  no: 17,
  cz: 18
} as const

export type CountryId = (typeof CountryIds)[keyof typeof CountryIds]

export const CountryByIds = {
  1: 'nl',
  2: 'be',
  3: 'de',
  4: 'at',
  5: 'gb',
  6: 'es',
  7: 'fr',
  8: 'se',
  9: 'dk',
  10: 'it',
  11: 'pl',
  12: 'ch',
  13: 'lu',
  14: 'ie',
  15: 'pt',
  16: 'fi',
  17: 'no',
  18: 'cz'
} as const

export const CountryIso3: CountryRecord<string> = {
  nl: 'NLD',
  be: 'BEL',
  de: 'DEU',
  at: 'AUT',
  gb: 'GBR',
  fr: 'FRA',
  es: 'ESP',
  se: 'SWE',
  dk: 'DNK',
  it: 'ITA',
  pl: 'POL',
  ch: 'CHE',
  ie: 'IRL',
  lu: 'LUX',
  pt: 'PRT',
  fi: 'FIN',
  no: 'NOR',
  cz: 'CZE'
} as const

export const ProductGroupIds = {
  originals: 1,
  one: 2,
  boys: 3
} as const

export const LocaleIsoCodes: LocaleRecord<string> = {
  de: 'de_DE',
  da: 'da_DK',
  es: 'es_ES',
  fr: 'fr_FR',
  nl: 'nl_NL',
  en: 'en_GB',
  sv: 'sv_SE',
  it: 'it_IT',
  pl: 'pl_PL',
  pt: 'pt_PT',
  fi: 'fi_FI',
  no: 'no_NO',
  cs: 'cs_CZ'
} as const

export const LanguageCountryCodes = {
  de: 'de_DE',
  at: 'de_AT',
  da: 'da_DK',
  es: 'es_ES',
  fr: 'fr_FR',
  nl: 'nl_NL',
  be: 'nl_BE',
  en: 'en_GB',
  sv: 'sv_SE',
  it: 'it_IT',
  pl: 'pl_PL',
  ch: 'DE_CH',
  ie: 'en_IE',
  lu: 'fr_LU',
  pt: 'pt_PT',
  fi: 'fi_FI',
  no: 'no_NO',
  cs: 'cs_CZ'
} as const

export const CountryCodesByLocale: LocaleRecord<string> = {
  de: 'DE',
  da: 'DK',
  es: 'ES',
  fr: 'FR',
  nl: 'NL',
  en: 'GB',
  sv: 'SE',
  it: 'IT',
  pl: 'PL',
  pt: 'PT',
  fi: 'FI',
  no: 'NO',
  cs: 'CZ'
} as const

export const CountryLocales = {
  at: 'de-AT',
  be: 'nl-BE',
  ch: 'de-CH',
  cz: 'cs-CZ',
  de: 'de-DE',
  dk: 'da-DK',
  es: 'es-ES',
  fi: 'fi-FI',
  fr: 'fr-FR',
  gb: 'en-GB',
  ie: 'en-IE',
  it: 'it-IT',
  lu: 'de-LU',
  nl: 'nl-NL',
  no: 'nb-NO',
  pl: 'pl-PL',
  pt: 'pt-PT',
  se: 'sv-SE'
} as const

export const ScreenBreakpoints = {
  lg: 1024,
  md: 768,
  container: 640,
  sm: 520,
  xsplus: 400,
  xs: 320
} as const

export const PaymentStatusses = {
  success: 'success',
  failed: 'failed',
  pending: 'pending'
} as const

export type PaymentStatus = (typeof PaymentStatusses)[keyof typeof PaymentStatusses]

export const QueryParams = {
  product: 'product',
  orientation: 'orientation',
  orientations: 'orientations',
  memberId: 'memberId',
  orderBundleId: 'orderBundleId'
} as const

export type QueryParamKey = keyof typeof QueryParams

export const TestPeriodDays: CountryRecord<number> = {
  [CountryCodes.austria]: 14,
  [CountryCodes.belgium]: 10,
  [CountryCodes.denmark]: 14,
  [CountryCodes.germany]: 14,
  [CountryCodes.spain]: 14,
  [CountryCodes.france]: 14,
  [CountryCodes.netherlands]: 10,
  [CountryCodes.unitedKingdom]: 14,
  [CountryCodes.sweden]: 14,
  [CountryCodes.italy]: 14,
  [CountryCodes.poland]: 14,
  [CountryCodes.switzerland]: 14,
  [CountryCodes.ireland]: 14,
  [CountryCodes.luxembourg]: 14,
  [CountryCodes.portugal]: 14,
  [CountryCodes.finland]: 14,
  [CountryCodes.norway]: 14,
  [CountryCodes.czechRepublic]: 14
} as const

export const DaysToSubtractFromDeliveryDate = 3 as const

